import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Add, ViewWeek, ViewList } from '@mui/icons-material';
import ClientList from '@/components/ClientList';
import ClientKanban from '@/components/ClientKanban';
import { Client, ClientSearchFilters, ClientStatus } from '@/types';
import { ClientService } from '@/services/clientService';

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');
  const [filters, setFilters] = useState<ClientSearchFilters>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClients();
  }, [filters]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await ClientService.listClients(filters);
      setClients(response.clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (client: Client, newStatus: ClientStatus) => {
    try {
      await ClientService.updateClient(client.id, { status: newStatus });
      fetchClients();
    } catch (error) {
      console.error('Failed to update client status:', error);
    }
  };

  const handleCreateClientClick = () => {
    navigate('/clients/create');
  };

  const handleClientClick = (client: Client) => {
    navigate(`/clients/${client.id}`);
  };

  const handleClientEdit = (client: Client) => {
    navigate(`/clients/${client.id}/edit`);
  };

  const handleClientDelete = async (client: Client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.profile.firstName} ${client.profile.lastName}"?`)) {
      return;
    }

    try {
      await ClientService.deleteClient(client.id, false); // Soft delete
      fetchClients();
    } catch (error: any) {
      alert(`Failed to delete client: ${error.message}`);
    }
  };

  const handleFilterChange = (newFilters: ClientSearchFilters) => {
    setFilters(newFilters);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <Typography variant="h3" sx={{ fontWeight: 600, color: 'text.primary', fontFamily: '"Playfair Display", serif' }}>
          Clients Pipeline
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="kanban">
              <ViewWeek sx={{ mr: 1 }} /> Kanban
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList sx={{ mr: 1 }} /> List
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={handleCreateClientClick}
            sx={{ boxShadow: 'none' }}
          >
            New Client
          </Button>
        </Box>
      </Box>

      {viewMode === 'kanban' ? (
        <ClientKanban 
          clients={clients} 
          onClientClick={handleClientClick} 
          onStatusChange={handleStatusChange}
        />
      ) : (
        <ClientList
          clients={clients}
          loading={loading}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClientClick={handleClientClick}
          onClientEdit={handleClientEdit}
          onClientDelete={handleClientDelete}
          showFilters={true}
        />
      )}

      <Fab
        color="primary"
        aria-label="add client"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreateClientClick}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default ClientsPage;
